/**
 * Gemini only accepts direct video files (MP4, WebM, MOV, etc.) — not HLS (.m3u8).
 */

const HLS_MIME_TYPES = new Set([
  "application/vnd.apple.mpegurl",
  "application/x-mpegurl",
  "audio/mpegurl",
  "audio/x-mpegurl",
]);

const GEMINI_VIDEO_EXTENSIONS = /\.(mp4|webm|mov|avi|mkv|m4v|3gp|mpeg|mpg|flv|wmv)(\?|$)/i;

export const isHlsUrl = (url = "") =>
  /\.m3u8(\?|$)/i.test(url) || /\/playlist\.m3u8/i.test(url);

export const isHlsMimeType = (mime = "") =>
  HLS_MIME_TYPES.has((mime || "").toLowerCase().split(";")[0].trim());

export const isGeminiCompatibleMime = (mime = "") => {
  const type = (mime || "").toLowerCase().split(";")[0].trim();
  if (!type) return false;
  if (isHlsMimeType(type)) return false;
  return type.startsWith("video/");
};

export const isGeminiCompatibleUrl = (url = "") => {
  if (!url || typeof url !== "string") return false;
  if (isHlsUrl(url)) return false;
  return GEMINI_VIDEO_EXTENSIONS.test(url);
};

/**
 * Bunny Stream HLS URLs often have a matching MP4 rendition on the same CDN path.
 * e.g. .../guid/playlist.m3u8 → .../guid/play_720p.mp4
 */
export const bunnyHlsToMp4Url = (hlsUrl) => {
  if (!isHlsUrl(hlsUrl)) return null;
  try {
    const parsed = new URL(hlsUrl);
    if (!parsed.hostname.includes("b-cdn.net")) return null;
    const base = hlsUrl.replace(/\/[^/]*\.m3u8.*$/i, "");
    return `${base}/play_720p.mp4`;
  } catch {
    return null;
  }
};

/**
 * Pick the best direct-video URL from a library item for Gemini analysis.
 * @returns {{ url: string|null, source: string|null }}
 */
export const pickGeminiVideoUrl = (video = {}) => {
  const candidates = [
    ["bunny_cdn_url", video.bunny_cdn_url],
    ["preview_url", video.preview_url],
    ["url", video.url],
    ["play_link", video.play_link],
    ["mp4_url", video.mp4_url],
    ["source_url", video.source_url],
  ];

  for (const [source, url] of candidates) {
    if (url && isGeminiCompatibleUrl(url)) {
      return { url, source };
    }
  }

  // bunny_cdn_url is sometimes a direct MP4 even without extension in the path
  if (video.bunny_cdn_url && !isHlsUrl(video.bunny_cdn_url)) {
    return { url: video.bunny_cdn_url, source: "bunny_cdn_url" };
  }

  // HLS-only Bunny video: DON'T guess an MP4 rendition here — a low-res upload
  // may only have 240p/360p, so a hardcoded 720p guess 404s. Pass the raw .m3u8
  // through and let the server probe which MP4 rendition actually exists
  // (see resolveBunnyMp4Url in server/lib/videoSource.js).
  const hlsUrl = video.HLSPlaylistURL || (isHlsUrl(video.url) ? video.url : null);
  if (hlsUrl && isHlsUrl(hlsUrl)) {
    return { url: hlsUrl, source: "bunny_hls" };
  }

  return { url: null, source: null };
};

export const GEMINI_HLS_ONLY_ERROR =
  "This video is HLS-encoded (.m3u8). Gemini needs a direct MP4 — upload the original file using “Main Video”, or pick a library item with an MP4 URL.";

export default pickGeminiVideoUrl;
